import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CheckCircle2, GripVertical, Pencil, Plus, Search, ShieldCheck, Trash2, XCircle } from "lucide-react";

const blankChannel = {
  nome: "",
  username: "",
  chat_id: "",
  categoria: "",
  proprietario: "",
  descrizione: "",
  logo: "",
  obbligatorio: true,
  attivo: true,
  ordine: 0,
  note: "",
};

function statusClass(channel) {
  if (channel.bot_admin) return "text-emerald-300";
  if (channel.bot_status === "Errore") return "text-red-300";
  return "text-amber-300";
}

export default function TelegramPartnerChannels() {
  const [channels, setChannels] = useState([]);
  const [meta, setMeta] = useState({ categories: [], owners: [], total: 0, active: 0, bot_admin: 0 });
  const [form, setForm] = useState(blankChannel);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [categoria, setCategoria] = useState("");
  const [attivo, setAttivo] = useState("");
  const [botAdmin, setBotAdmin] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragId, setDragId] = useState("");
  const [verifyingId, setVerifyingId] = useState("");

  const load = useCallback(async (filters = {}) => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.categoria) params.categoria = filters.categoria;
    if (filters.attivo) params.attivo = filters.attivo;
    if (filters.botAdmin) params.bot_admin = filters.botAdmin;
    const [channelsRes, metaRes] = await Promise.all([
      api.get("/telegram/partner-channels", { params }),
      api.get("/telegram/partner-channels/meta"),
    ]);
    setChannels(channelsRes.data || []);
    setMeta(metaRes.data || {});
  }, []);

  const reload = () => load({ search, categoria, attivo, botAdmin });

  useEffect(() => {
    load().catch(() => toast.error("Errore caricamento canali partner"));
  }, [load]);

  const filteredLabel = useMemo(() => {
    const parts = [];
    if (search) parts.push(`ricerca "${search}"`);
    if (categoria) parts.push(categoria);
    if (attivo) parts.push(attivo === "true" ? "attivi" : "disattivati");
    if (botAdmin) parts.push(botAdmin === "true" ? "bot admin" : "bot non admin");
    return parts.join(" · ");
  }, [search, categoria, attivo, botAdmin]);

  const resetForm = () => {
    setEditingId("");
    setForm(blankChannel);
  };

  const save = async () => {
    setLoading(true);
    try {
      if (editingId) {
        await api.patch(`/telegram/partner-channels/${editingId}`, form);
        toast.success("Canale aggiornato");
      } else {
        await api.post("/telegram/partner-channels", form);
        toast.success("Canale aggiunto");
      }
      resetForm();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio canale");
    } finally {
      setLoading(false);
    }
  };

  const edit = (channel) => {
    setEditingId(channel.id);
    setForm({ ...blankChannel, ...channel });
  };

  const remove = async (channel) => {
    if (!window.confirm(`Eliminare ${channel.nome || channel.username}?`)) return;
    try {
      await api.delete(`/telegram/partner-channels/${channel.id}`);
      toast.success("Canale eliminato");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const toggle = async (channel, value) => {
    try {
      await api.patch(`/telegram/partner-channels/${channel.id}`, { attivo: value });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento stato");
    }
  };

  const verifyBot = async (channel) => {
    setVerifyingId(channel.id);
    try {
      const { data } = await api.post(`/telegram/partner-channels/${channel.id}/verify-bot`);
      if (data.ok) toast.success(data.message || "Bot verificato");
      else toast.error(data.message || "Verifica bot non riuscita");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verifica bot non riuscita");
    } finally {
      setVerifyingId("");
    }
  };

  const persistOrder = async (items) => {
    setChannels(items);
    try {
      await api.post("/telegram/partner-channels/reorder", { ids: items.map(item => item.id) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio ordine");
      await reload();
    }
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const from = channels.findIndex(item => item.id === dragId);
    const to = channels.findIndex(item => item.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...channels];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDragId("");
    await persistOrder(next);
  };

  return (
    <div data-testid="telegram-partner-channels-page">
      <PageHeader
        title="Telegram Partner Channels"
        subtitle="Gestisci i canali partner usati dal Telegram Access Bot e dalla futura verifica iscrizioni."
      />
      <PageContent>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
            <div className="text-xs text-slate-500">Totali</div>
            <div className="text-2xl font-bold text-white">{meta.total || 0}</div>
          </div>
          <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
            <div className="text-xs text-slate-500">Attivi</div>
            <div className="text-2xl font-bold text-white">{meta.active || 0}</div>
          </div>
          <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
            <div className="text-xs text-slate-500">Bot admin</div>
            <div className="text-2xl font-bold text-white">{meta.bot_admin || 0}</div>
          </div>
          <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
            <div className="text-xs text-slate-500">Filtro</div>
            <div className="truncate text-sm text-slate-300">{filteredLabel || "Nessun filtro"}</div>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[420px_1fr]">
          <section className="rounded-md border border-slate-800 bg-slate-900 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-bold text-white">{editingId ? "Modifica canale" : "Aggiungi canale"}</h3>
              {editingId && <Button type="button" size="sm" variant="outline" onClick={resetForm}>Nuovo</Button>}
            </div>
            <div className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input value={form.nome || ""} onChange={e => setForm({ ...form, nome: e.target.value })} className="mt-1 border-slate-800 bg-slate-950" placeholder="Calcio News 24" />
              </div>
              <div>
                <Label>Username</Label>
                <Input value={form.username || ""} onChange={e => setForm({ ...form, username: e.target.value })} className="mt-1 border-slate-800 bg-slate-950" placeholder="@canale" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Categoria</Label>
                  <Input value={form.categoria || ""} onChange={e => setForm({ ...form, categoria: e.target.value })} className="mt-1 border-slate-800 bg-slate-950" placeholder="News" />
                </div>
                <div>
                  <Label>Proprietario</Label>
                  <Input value={form.proprietario || ""} onChange={e => setForm({ ...form, proprietario: e.target.value })} className="mt-1 border-slate-800 bg-slate-950" placeholder="Network" />
                </div>
              </div>
              <div>
                <Label>Logo</Label>
                <Input value={form.logo || ""} onChange={e => setForm({ ...form, logo: e.target.value })} className="mt-1 border-slate-800 bg-slate-950" placeholder="https://..." />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea value={form.descrizione || ""} onChange={e => setForm({ ...form, descrizione: e.target.value })} className="mt-1 border-slate-800 bg-slate-950" rows={3} />
              </div>
              <div>
                <Label>Note</Label>
                <Textarea value={form.note || ""} onChange={e => setForm({ ...form, note: e.target.value })} className="mt-1 border-slate-800 bg-slate-950" rows={2} />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950 p-3">
                <Label>Attivo</Label>
                <Switch checked={!!form.attivo} onCheckedChange={value => setForm({ ...form, attivo: value })} />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950 p-3">
                <Label>Obbligatorio per accesso live</Label>
                <Switch checked={form.obbligatorio !== false} onCheckedChange={value => setForm({ ...form, obbligatorio: value })} />
              </div>
              <Button type="button" onClick={save} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
                <Plus size={16} /> {loading ? "Salvataggio..." : editingId ? "Salva modifiche" : "Aggiungi canale"}
              </Button>
            </div>
          </section>

          <section className="space-y-3">
            <div className="rounded-md border border-slate-800 bg-slate-900 p-4">
              <div className="grid gap-3 lg:grid-cols-[1fr_180px_150px_150px_auto]">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-3 text-slate-500" size={16} />
                  <Input value={search} onChange={e => setSearch(e.target.value)} className="border-slate-800 bg-slate-950 pl-9" placeholder="Cerca canale, proprietario, categoria..." />
                </div>
                <select value={categoria} onChange={e => setCategoria(e.target.value)} className="rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-100">
                  <option value="">Tutte le categorie</option>
                  {(meta.categories || []).map(item => <option key={item} value={item}>{item}</option>)}
                </select>
                <select value={attivo} onChange={e => setAttivo(e.target.value)} className="rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-100">
                  <option value="">Tutti</option>
                  <option value="true">Attivi</option>
                  <option value="false">Disattivati</option>
                </select>
                <select value={botAdmin} onChange={e => setBotAdmin(e.target.value)} className="rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-100">
                  <option value="">Bot admin</option>
                  <option value="true">Si</option>
                  <option value="false">No</option>
                </select>
                <Button type="button" variant="outline" onClick={reload}>Filtra</Button>
              </div>
            </div>

            <div className="space-y-3">
              {channels.length === 0 ? (
                <div className="rounded-md border border-slate-800 bg-slate-900 p-8 text-center text-sm text-slate-500">
                  Nessun canale partner configurato.
                </div>
              ) : channels.map(channel => (
                <article
                  key={channel.id}
                  draggable
                  onDragStart={() => setDragId(channel.id)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => onDrop(channel.id)}
                  className="rounded-md border border-slate-800 bg-slate-900 p-4"
                >
                  <div className="grid gap-4 xl:grid-cols-[32px_1fr_auto]">
                    <button type="button" className="hidden cursor-grab items-center justify-center text-slate-500 xl:flex" title="Trascina per ordinare">
                      <GripVertical size={18} />
                    </button>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        {channel.logo ? (
                          <img src={channel.logo} alt="" className="h-10 w-10 rounded-md border border-slate-800 object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-800 bg-slate-950 text-lg">📢</div>
                        )}
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-white">{channel.nome || channel.username || "Canale partner"}</div>
                          <div className="text-xs text-slate-500">{channel.username || "-"} · Chat ID: {channel.chat_id || "da recuperare"}</div>
                        </div>
                        <span className={`rounded border px-2 py-1 text-xs ${channel.attivo ? "border-emerald-500/30 text-emerald-300" : "border-slate-700 text-slate-400"}`}>
                          {channel.attivo ? "Attivo" : "Disattivato"}
                        </span>
                        <span className={`rounded border px-2 py-1 text-xs ${channel.obbligatorio !== false ? "border-blue-500/30 text-blue-300" : "border-slate-700 text-slate-400"}`}>
                          {channel.obbligatorio !== false ? "Obbligatorio" : "Facoltativo"}
                        </span>
                        <span className={`rounded border border-slate-700 px-2 py-1 text-xs ${statusClass(channel)}`}>
                          {channel.bot_admin ? "Bot amministratore" : channel.bot_status || "Non verificato"}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-slate-400 sm:grid-cols-2 xl:grid-cols-4">
                        <div>Categoria: <span className="text-slate-200">{channel.categoria || "-"}</span></div>
                        <div>Proprietario: <span className="text-slate-200">{channel.proprietario || "-"}</span></div>
                        <div>Click: <span className="text-slate-200">{channel.click_totali || 0}</span></div>
                        <div>Utenti verificati: <span className="text-slate-200">{channel.utenti_verificati || 0}</span></div>
                        <div>Accessi live: <span className="text-slate-200">{channel.accessi_live || 0}</span></div>
                        <div>Conversioni: <span className="text-slate-200">{channel.conversioni || 0}</span></div>
                        <div className="sm:col-span-2">Ultima attività: <span className="text-slate-200">{channel.ultima_attivita || "-"}</span></div>
                      </div>
                      {channel.bot_status_message && <div className="mt-2 text-xs text-slate-500">{channel.bot_status_message}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2 xl:justify-end">
                      <Button type="button" size="sm" variant="outline" onClick={() => verifyBot(channel)} disabled={verifyingId === channel.id}>
                        <ShieldCheck size={14} /> {verifyingId === channel.id ? "Verifica..." : "Verifica Bot"}
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => toggle(channel, !channel.attivo)}>
                        {channel.attivo ? <XCircle size={14} /> : <CheckCircle2 size={14} />} {channel.attivo ? "Disattiva" : "Attiva"}
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => edit(channel)}><Pencil size={14} /> Modifica</Button>
                      <Button type="button" size="sm" variant="ghost" className="text-red-300 hover:text-red-200" onClick={() => remove(channel)}><Trash2 size={14} /> Elimina</Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      </PageContent>
    </div>
  );
}
